
import { useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import { AppIcon } from "./AppIcon";
import { type AppInfo } from "@/hooks/useNetworkRules";

interface AppSelectorProps {
  selectedApps: AppInfo[];
  onAppsChange: (apps: AppInfo[]) => void;
  disabled?: boolean;
  maxSelection?: number;
}

export const AppSelector = ({ selectedApps, onAppsChange, disabled, maxSelection }: AppSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Fetch apps using Convex
  const appsData = useConvexQuery(api.apps.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100
    },
    query: searchTerm
  });

  const apps = appsData?.page || [];

  const handleAppSelect = (app: any) => {
    const appInfo: AppInfo = {
      bundleId: app.bundle_id || app.bundleId || '',
      appName: app.name || app.app_name || 'Unknown App',
      iconUrl: app.iconUrl || app.app_icon_url,
      trackId: app.trackId || null,
      id: app.id || app._id
    };
    
    // If maxSelection is 1, replace the current selection, otherwise add to array
    if (maxSelection === 1) {
      onAppsChange([appInfo]);
    } else {
      const updatedApps = [...selectedApps, appInfo];
      onAppsChange(updatedApps);
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleAppRemove = (bundleId: string) => {
    // Remove the app from the selected apps array
    const updatedApps = selectedApps.filter(app => app.bundleId !== bundleId);
    onAppsChange(updatedApps);
  };

  const filteredApps = apps.filter(app => 
    !selectedApps.some(selected => 
      selected.bundleId === app.bundleId
    )
  );

  return (
    <div className="space-y-4">
      <div>
        {/* Selected Apps Display */}
        {selectedApps.length > 0 && (
          <div className="mb-3 space-y-2">
            {selectedApps.map((app) => (
              <div key={app.bundleId} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AppIcon app_icon_url={app.iconUrl} app_name={app.appName} />
                  <div>
                    <div className="font-medium text-sm">{app.appName}</div>
                    <div className="text-xs text-gray-500">{app.bundleId}</div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleAppRemove(app.bundleId)}
                  className="text-red-600 hover:text-red-800"
                  disabled={disabled}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* App Selector Dropdown */}
        <div className="relative">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsOpen(!isOpen)}
            className="w-full justify-between"
            disabled={disabled || (maxSelection === 1 && selectedApps.length >= 1)}
          >
            <span>{maxSelection === 1 && selectedApps.length > 0 ? selectedApps[0].appName : "Select an app..."}</span>
            <ChevronDown className="w-4 h-4" />
          </Button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
              <div className="p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search apps..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    disabled={disabled}
                  />
                </div>
              </div>
              
              <ScrollArea className="h-60">
                {filteredApps.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {searchTerm ? 'No apps found' : 'No available apps'}
                  </div>
                ) : (
                  <div className="p-1">
                    {filteredApps.map((app) => (
                      <button
                        key={app.bundleId}
                        type="button"
                        onClick={() => handleAppSelect(app)}
                        className="w-full p-2 text-left hover:bg-gray-50 rounded flex items-center space-x-3"
                        disabled={disabled}
                      >
                        <AppIcon 
                          app_icon_url={app.iconUrl} 
                          app_name={app.name} 
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">
                            {app.name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {app.bundleId}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
